import { useEffect, useState } from 'react';
import { X, Check, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { DateRangeField } from '@/shared/ui/components';
import { useDebounce } from '@/shared/hooks/use-debounce';
import {
  usePatientSearch,
  type PatientSearchResult,
} from '@/services/scheduling/use-patient-search';
import {
  LAB_ORDER_STATUS,
  type LabOrderStatus,
} from '@/types/scheduling-api';

/**
 * OrderRequestsFilterPanel — port of Flutter `OrderRequestsFilter`
 * (`scheduling_module/.../common_widgets/order_requests_filter.dart`,
 * 316 lines). 5 sections, matching Flutter visual order verbatim:
 *
 *   1. Patient name autocomplete   (writes patientId)
 *   2. MRN autocomplete            (writes patientId — picks same field)
 *   3. Order Status dropdown
 *   4. From Date / To Date
 *
 * The Patient and MRN inputs are **mutually exclusive** (Flutter
 * `:120-122` / `:159-161`): selecting one clears the other. Both
 * pickers ultimately write to the SAME wire field `patientId` (Flutter
 * `order_requests_tab.dart:138` `patientId: filterModel.mrn?.id ??
 * filterModel.patientId?.id`). The backend looks up by GUID; the UI
 * just gives the user two affordances for finding the patient (name
 * vs MRN). The chip strip (PROD-908) needs to remember which path was
 * used so it labels the chip "Patient: …" vs "MRN: …" — hence the
 * separate `patientName` / `mrnLabel` UI-only fields below.
 *
 * State strategy mirrors the canonical filter pattern
 * (`documents-filter-panel.tsx`): the panel owns a `draft` copy of
 * the parent's `value` prop; the table-driving `useLabOrdersList`
 * never sees the draft (the parent's `filters` state only updates
 * when `onApply` fires). Close-without-Done discards in-flight
 * draft edits — they never reach the wire.
 *
 * PHI / network notes for the Patient + MRN autocompletes
 * (PR #341 review §2):
 *   - The autocomplete input is wrapped in `useDebounce(query)` —
 *     default 300ms (`src/shared/hooks/use-debounce.ts`).
 *   - `usePatientSearch.enabled` only fires for `trimmed.length >= 2`,
 *     so single keystrokes never hit `/api/Patients/GetPatientList`.
 *   - The hook's `queryFn` forwards React Query's `AbortSignal` to
 *     `get(…, { signal })` — every keystroke that mutates the
 *     `queryKey` cancels the in-flight fetch, so PHI responses
 *     can't race a newer query.
 */

export interface OrderRequestsFilters {
  /** Wire `patientId` — set by either picker (mutually exclusive). */
  patientId?: string;
  /** UI-only — display label for the Patient name chip. Only set when
   *  the user picked from the Patient name input. */
  patientName?: string;
  /** UI-only — display label for the MRN chip. Only set when the user
   *  picked from the MRN input. */
  mrnLabel?: string;
  /** Wire `status` — verbatim from `LAB_ORDER_STATUS`. */
  status?: LabOrderStatus;
  /** Wire `fromDate` — `YYYY-MM-DD`. */
  fromDate?: string;
  /** Wire `toDate` — `YYYY-MM-DD`. */
  toDate?: string;
  /** Wire `labVendorId` (PROD-2653) — set by the tab's Lab vendor select,
   *  not by this panel. The panel's draft spreads it through untouched. */
  labVendorId?: string;
}

export const EMPTY_ORDER_REQUESTS_FILTERS: OrderRequestsFilters = {
  patientId: undefined,
  patientName: undefined,
  mrnLabel: undefined,
  status: undefined,
  fromDate: undefined,
  toDate: undefined,
  labVendorId: undefined,
};

const STATUS_OPTIONS: ReadonlyArray<{ label: string; value: LabOrderStatus }> = [
  { label: 'Requested', value: LAB_ORDER_STATUS.Requested },
  { label: 'Sample collected', value: LAB_ORDER_STATUS.SampleCollected },
  { label: 'Result received', value: LAB_ORDER_STATUS.ResultReceived },
  { label: 'Result reviewed', value: LAB_ORDER_STATUS.ResultReviewed },
  { label: 'Cancelled', value: LAB_ORDER_STATUS.Cancelled },
];

interface OrderRequestsFilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: OrderRequestsFilters;
  onApply: (next: OrderRequestsFilters) => void;
  /** Org id for the autocomplete query — comes from the auth store at the
   *  call site; passed in so the panel stays auth-agnostic for stories. */
  organizationId: string;
}

export function OrderRequestsFilterPanel({
  open,
  onOpenChange,
  value,
  onApply,
  organizationId,
}: OrderRequestsFilterPanelProps) {
  const [draft, setDraft] = useState<OrderRequestsFilters>(value);

  // Reset draft to incoming value whenever the sheet opens — discards
  // any half-finished edit from a previous open-close cycle.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);


  const hasPatient = !!draft.patientId;
  const hasStatus = !!draft.status;
  const hasDates = !!(draft.fromDate || draft.toDate);

  const resetPatient = () =>
    setDraft((d) => ({
      ...d,
      patientId: undefined,
      patientName: undefined,
      mrnLabel: undefined,
    }));
  const resetStatus = () => setDraft((d) => ({ ...d, status: undefined }));
  const resetDates = () =>
    setDraft((d) => ({ ...d, fromDate: undefined, toDate: undefined }));

  const handlePickPatientByName = (p: PatientSearchResult) => {
    const name = `${p.firstName} ${p.lastName}`.trim();
    setDraft((d) => ({
      ...d,
      patientId: p.id,
      patientName: name || p.mrn || p.id,
      // Mutually exclusive — Flutter :120-122 clears MRN when picking
      // by Name.
      mrnLabel: undefined,
    }));
  };

  const handlePickPatientByMrn = (p: PatientSearchResult) => {
    setDraft((d) => ({
      ...d,
      patientId: p.id,
      // Mutually exclusive — Flutter :159-161 clears Patient when
      // picking by MRN.
      patientName: undefined,
      mrnLabel: p.mrn ?? p.id,
    }));
  };

  // From-date can never be > to-date (Flutter `:253` `firstDate/lastDate`
  // bounds). The `min/max` on the inputs gives the user that constraint
  // without inventing a new toast.

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="data-[side=right]:w-[424px] data-[side=right]:!max-w-none p-0 gap-0 flex flex-col bg-surface-background"
      >
        <div className="flex items-center justify-between px-6 h-12 bg-surface-card border-b border-border-default shrink-0">
          <SheetTitle className="text-base font-bold text-text-primary">
            Filters
          </SheetTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close filters"
            className="text-text-muted hover:text-text-primary"
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scrollbar-hide px-6 py-6 space-y-6">
          {/* Section 1 — Patient name + Section 2 — MRN (grouped under
              one Reset because picking either populates the same wire
              field — clearing one without the other would leave a
              dangling label on the chip strip). */}
          <Section
            label="Patient name"
            onReset={hasPatient ? resetPatient : undefined}
          >
            <PatientAutocomplete
              mode="name"
              organizationId={organizationId}
              activeLabel={draft.patientName}
              onPick={handlePickPatientByName}
              onClear={resetPatient}
            />
          </Section>

          <Section label="MRN">
            <PatientAutocomplete
              mode="mrn"
              organizationId={organizationId}
              activeLabel={draft.mrnLabel}
              onPick={handlePickPatientByMrn}
              onClear={resetPatient}
            />
          </Section>

          {/* Section 3 — Order Status */}
          <Section
            label="Order status"
            onReset={hasStatus ? resetStatus : undefined}
          >
            <ul className="space-y-2">
              {STATUS_OPTIONS.map((opt) => (
                <li key={opt.value}>
                  <CheckRow
                    label={opt.label}
                    checked={draft.status === opt.value}
                    onToggle={() =>
                      setDraft((d) => ({
                        ...d,
                        // Click an already-selected option to clear
                        // — matches Flutter dropdown's optional state.
                        status: d.status === opt.value ? undefined : opt.value,
                      }))
                    }
                  />
                </li>
              ))}
            </ul>
          </Section>

          {/* Section 4 — Request date range */}
          <Section
            label="Request date"
            onReset={hasDates ? resetDates : undefined}
          >
            <DateRangeField
              startDate={draft.fromDate}
              endDate={draft.toDate}
              max={new Date()}
              onChange={(from, to) =>
                setDraft((d) => ({ ...d, fromDate: from, toDate: to }))
              }
            />
          </Section>
        </div>

        <div className="px-6 py-3 border-t border-border-default flex items-center justify-between gap-3 bg-surface-card">
          <button
            type="button"
            onClick={() => {
              setDraft(EMPTY_ORDER_REQUESTS_FILTERS);
              onApply(EMPTY_ORDER_REQUESTS_FILTERS);
              onOpenChange(false);
            }}
            className="text-sm text-text-secondary hover:text-text-primary"
          >
            Reset all
          </button>
          <button
            type="button"
            onClick={() => {
              onApply(draft);
              onOpenChange(false);
            }}
            className="h-10 px-6 rounded-full bg-brand-depth hover:bg-brand-primary text-white text-sm font-medium"
          >
            Done
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({
  label,
  onReset,
  children,
}: {
  label: string;
  onReset?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-text-muted">
          {label}
        </span>
        {onReset !== undefined ? (
          <button
            type="button"
            onClick={onReset}
            className="text-sm font-medium text-brand-primary hover:text-brand-depth"
          >
            Reset
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function CheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 text-left"
    >
      <span
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full border transition-colors',
          checked
            ? 'border-brand-primary bg-brand-primary'
            : 'border-border-default bg-transparent',
        )}
      >
        {checked ? (
          <Check className="h-3 w-3 text-white" strokeWidth={3} aria-hidden />
        ) : null}
      </span>
      <span className="text-sm text-text-primary">{label}</span>
    </button>
  );
}

/**
 * PatientAutocomplete — debounced GetPatientList typeahead. The
 * backend `/api/Patients/GetPatientList` (the same endpoint
 * `usePatientSearch` already wraps) ranks across both name and MRN
 * — so a single search-list serves both filter inputs. The visible
 * row format is tweaked per-mode to bring the relevant field forward:
 *   - `mode="name"` — "Khadija Devtest · MRN 000-099-9997"
 *   - `mode="mrn"`  — "MRN 000-099-9997 · Khadija Devtest"
 *
 * Active selection is rendered as a removable pill (rather than back
 * inside the input) so it's visually distinct from the in-progress
 * search and clears in one click.
 */
function PatientAutocomplete({
  mode,
  organizationId,
  activeLabel,
  onPick,
  onClear,
}: {
  mode: 'name' | 'mrn';
  organizationId: string;
  activeLabel?: string;
  onPick: (p: PatientSearchResult) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const debounced = useDebounce(query);
  const { data: results = [], isFetching } = usePatientSearch({
    organizationId,
    query: debounced,
  });

  // Hide the dropdown once a pick is made (parent clears `query`
  // on its end via `onPick` → next render shows the active pill).
  const showDropdown = focused && debounced.trim().length >= 2;

  if (activeLabel) {
    return (
      <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm">
        <span>{activeLabel}</span>
        <button
          type="button"
          onClick={onClear}
          aria-label={
            mode === 'mrn' ? 'Clear MRN filter' : 'Clear patient filter'
          }
          className="w-4 h-4 inline-flex items-center justify-center rounded-full text-blue-500 hover:bg-blue-500/15"
        >
          <X className="w-3 h-3" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 h-10 px-3 bg-grey-100 rounded-md text-sm focus-within:ring-2 focus-within:ring-brand-primary">
        <Search className="w-4 h-4 text-text-muted" aria-hidden />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          // Delay blur so click on a dropdown row still registers.
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          placeholder={mode === 'mrn' ? 'Search MRN' : 'Search patient'}
          className="flex-1 bg-transparent outline-none"
          maxLength={100}
        />
        {isFetching ? (
          <Loader2 className="w-4 h-4 animate-spin text-text-muted" aria-hidden />
        ) : null}
      </div>
      {showDropdown ? (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-auto scrollbar-hide rounded-md border border-border-default bg-surface-card shadow-lg">
          {results.length === 0 && !isFetching ? (
            <p className="px-3 py-2 text-sm text-text-muted">
              No matches.
            </p>
          ) : (
            <ul>
              {results.map((p) => {
                const name = `${p.firstName} ${p.lastName}`.trim();
                const mrn = p.mrn ?? '';
                const primary = mode === 'mrn' ? mrn || name : name;
                const secondary = mode === 'mrn' ? name : mrn;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onPick(p);
                        setQuery('');
                        setFocused(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-grey-100"
                    >
                      <span className="text-sm text-text-primary">
                        {primary || '(unnamed)'}
                      </span>
                      {secondary ? (
                        <span className="block text-xs text-text-muted">
                          {secondary}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
